import React, { FC, useMemo, useCallback } from 'react';
import {
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import styled from 'styled-components';
import { TruthTableEntry } from '../../../common/model/dto/lot-calibration-data';
import Colours from '../../../design-system/colours';
import { useTranslation } from 'react-i18next';
import OvCalibrationChartTooltip from './OvCalibrationChartTooltip';
import { calculateDomainWithPadding } from '../../../utils/chart-domain-calculation';

interface OvCalibrationTruthTableChartProps {
  data: TruthTableEntry[];
  thresholds?: number[];
  unit: string;
}

// Chart configuration constants
const CHART_HEIGHT = 400;
const PADDING_PERCENT = 0.25; // 25% padding for domain calculation (matches candlestick chart)

const OvCalibrationTruthTableChart: FC<OvCalibrationTruthTableChartProps> = ({
  data,
  thresholds,
  unit,
}) => {
  const { t } = useTranslation();

  /**
   * Formats a concentration value for display
   */
  const formatConcentration = useCallback((value: number): string => {
    if (value < 1) return value.toFixed(2);
    if (value < 100) return value.toFixed(1);
    return Math.round(value).toString();
  }, []);

  const chartData = useMemo(() => {
    // Map data to include all original entry properties for tooltip access
    return data.map((entry) => ({
      ...entry,
      signal: entry.signal,
      concentration: entry.concentration,
    }));
  }, [data]);

  // Calculate domain to match candlestick chart (before early return)
  // Include all concentration values plus potential error margins if available
  const { domainMin, domainMax } = useMemo(() => {
    const allValues = data.flatMap((entry) => {
      const values = [entry.concentration];
      if (entry.concentrationPlusError !== undefined) {
        values.push(entry.concentrationPlusError);
      }
      if (entry.concentrationMinusError !== undefined) {
        values.push(entry.concentrationMinusError);
      }
      return values;
    });
    
    return calculateDomainWithPadding(allValues, PADDING_PERCENT);
  }, [data]);

  /**
   * Extract data from line chart payload for tooltip
   */
  const extractLineChartData = useCallback(
    (payload: unknown) => {
      const entry = payload as TruthTableEntry;
      return {
        signal: entry.signal,
        base: entry.concentration,
        high: entry.concentrationPlusError,
        low: entry.concentrationMinusError,
        unit,
        formatConcentration,
      };
    },
    [unit, formatConcentration]
  );

  // Check if we have error margin data to display uncertainty area
  const hasErrorData = useMemo(() => {
    return data.some(
      (entry) =>
        entry.concentrationPlusError !== undefined &&
        entry.concentrationMinusError !== undefined
    );
  }, [data]);

  // Early return after all hooks
  if (!chartData || chartData.length === 0) {
    return null;
  }

  return (
    <Container>
      <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
        <ComposedChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={Colours.GRAY['200']} />
          <XAxis
            dataKey="signal"
            label={{
              value: t('lots.calibration.chart.signal'),
              position: 'insideBottom',
              offset: -5,
              fill: Colours.OV_BASE,
            }}
            tick={{ fill: Colours.OV_BASE }}
          />
          <YAxis
            domain={[domainMin, domainMax]}
            label={{
              value: unit && unit !== 'null' && unit !== 'undefined' && unit.trim() !== ''
                ? `${t('lots.calibration.chart.concentration')} (${unit})`
                : t('lots.calibration.chart.concentration'),
              angle: -90,
              position: 'insideLeft',
              fill: Colours.OV_BASE,
            }}
            tick={{ fill: Colours.OV_BASE }}
            width={80}
          />
          <Tooltip
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            content={(props: any) => (
              <OvCalibrationChartTooltip
                {...props}
                unit={unit}
                formatConcentration={formatConcentration}
                t={t}
                extractData={extractLineChartData}
              />
            )}
          />
          {/* Threshold Lines */}
          {thresholds?.map((threshold, index) => (
            <ReferenceLine
              key={`threshold-${index}`}
              y={threshold}
              stroke={Colours.OV_RED}
              strokeDasharray="5 5"
              label={{
                value: `${threshold} ${unit}`,
                position: 'right',
                fill: Colours.OV_RED,
              }}
            />
          ))}
          {/* Uncertainty area - shows the range between high and low */}
          {hasErrorData && (
            <>
              {/* Upper boundary line (high) */}
              <Line
                type="monotone"
                dataKey="concentrationPlusError"
                stroke={Colours.GRAY['400']}
                strokeWidth={1}
                strokeDasharray="2 2"
                dot={false}
                connectNulls={true}
                strokeOpacity={0.5}
              />
              {/* Lower boundary line (low) */}
              <Line
                type="monotone"
                dataKey="concentrationMinusError"
                stroke={Colours.GRAY['400']}
                strokeWidth={1}
                strokeDasharray="2 2"
                dot={false}
                connectNulls={true}
                strokeOpacity={0.5}
              />
            </>
          )}
          {/* Base concentration line */}
          <Line
            type="monotone"
            dataKey="concentration"
            stroke={Colours.OV_BASE}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 6, fill: Colours.OV_BASE }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </Container>
  );
};

export default OvCalibrationTruthTableChart;

const Container = styled.div`
  margin-top: 1rem;
  width: 100%;
`;

